import { useState, useRef, useEffect } from "react";
import { MessageSquare, X, Send, Loader2, Minimize2, RotateCcw, Mic, Square, MicOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatBubble({ externalOpen, onOpenChange }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;
  
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [hasHistory, setHasHistory] = useState(false);
  
  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, open]);

  useEffect(() => {
    if (open && inputRef.current && !isRecording) inputRef.current.focus();
  }, [open, isRecording]);

  // Load session and history when opening
  useEffect(() => {
    if (open && !sessionId) {
      initSession();
    }
  }, [open]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recordingIntervalRef.current) clearInterval(recordingIntervalRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  const initSession = async () => {
    try {
      const res = await axios.post(`${API}/chat/session`);
      setSessionId(res.data.session_id);
      setHasHistory(res.data.has_history);
      
      if (res.data.has_history) {
        loadHistory();
      }
    } catch (e) {
      console.error("Failed to init session:", e);
    }
  };

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API}/chat/history`);
      if (res.data.messages && res.data.messages.length > 0) {
        setMessages(res.data.messages.map(m => ({
          role: m.role,
          text: m.content,
        })));
      }
    } catch (e) {
      console.error("Failed to load history:", e);
    } finally {
      setLoadingHistory(false);
    }
  };

  const clearHistory = async () => {
    try {
      await axios.delete(`${API}/chat/history`);
      setMessages([]);
      setHasHistory(false);
      toast.success("Chat history cleared");
    } catch (e) {
      toast.error("Failed to clear history");
    }
  };

  const sendMessage = async (text) => {
    if (!text?.trim() || loading) return;
    const messageText = text.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", text: messageText }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/chat/message`, {
        message: messageText,
        session_id: sessionId || "temp",
      });
      setMessages(prev => [...prev, { role: "assistant", text: res.data.response }]);
      setHasHistory(true);
    } catch (e) {
      setMessages(prev => [...prev, { role: "assistant", text: "Sorry, I couldn't process that. Please try again." }]);
    }
    setLoading(false);
  };

  const handleSendClick = () => {
    sendMessage(input);
  };

  // Voice recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });
      
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = async () => {
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        
        // Create blob and transcribe
        const audioBlob = new Blob(audioChunksRef.current, { 
          type: mediaRecorderRef.current.mimeType 
        });
        
        await transcribeAudio(audioBlob);
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (err) {
      console.error("Failed to start recording:", err);
      toast.error("Could not access microphone. Please check permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setIsTranscribing(true);
    
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');
      
      const res = await axios.post(`${API}/voice/transcribe`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const transcribedText = res.data.text;
      
      if (transcribedText && transcribedText.trim()) {
        // Auto-send the transcribed message
        await sendMessage(transcribedText);
      } else {
        toast.error("Could not understand the audio. Please try again.");
      }
    } catch (err) {
      console.error("Transcription failed:", err);
      toast.error("Failed to transcribe audio. Please try again.");
    } finally {
      setIsTranscribing(false);
      setRecordingTime(0);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const toggleOpen = () => {
    setOpen(!open);
  };

  return (
    <>
      {/* Chat Panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-20 md:bottom-20 right-3 sm:right-5 z-50 w-[calc(100vw-1.5rem)] sm:w-96 h-[calc(100vh-10rem)] sm:h-[500px] bg-white rounded-2xl shadow-2xl shadow-stone-900/15 border border-stone-200/80 flex flex-col overflow-hidden"
          style={{ maxHeight: "calc(100vh - 120px)", maxWidth: "400px" }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-stone-900 text-white">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              <span className="text-sm font-semibold">AI Consultant</span>
              {hasHistory && (
                <span className="text-[10px] px-1.5 py-0.5 bg-white/20 rounded-full">Memory On</span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && (
                <button 
                  data-testid="chat-clear" 
                  onClick={clearHistory} 
                  className="p-1.5 hover:bg-white/10 rounded transition-colors"
                  title="Clear history & start fresh"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                </button>
              )}
              <button data-testid="chat-minimize" onClick={toggleOpen} className="p-1.5 hover:bg-white/10 rounded transition-colors">
                <Minimize2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {loadingHistory ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-stone-400 mr-2" />
                <span className="text-sm text-stone-500">Loading history...</span>
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center py-8">
                <MessageSquare className="w-8 h-8 text-stone-200 mx-auto mb-3" />
                <p className="text-sm text-stone-500 font-medium">Hi! I'm your AI Consultant.</p>
                <p className="text-xs text-stone-400 mt-1">Type a message or tap the mic to speak.</p>
              </div>
            ) : (
              messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-3.5 py-2 rounded-2xl text-sm leading-relaxed ${
                    msg.role === "user"
                      ? "bg-stone-900 text-white rounded-br-md"
                      : "bg-stone-100 text-stone-800 rounded-bl-md"
                  }`}>
                    <p className="whitespace-pre-wrap">{msg.text}</p>
                  </div>
                </div>
              ))
            )}
            {(loading || isTranscribing) && (
              <div className="flex justify-start">
                <div className="bg-stone-100 px-4 py-2.5 rounded-2xl rounded-bl-md flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-stone-400" />
                  <span className="text-xs text-stone-500">
                    {isTranscribing ? "Transcribing..." : "Thinking..."}
                  </span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t border-stone-100 bg-white">
            {isRecording ? (
              // Recording UI
              <div className="flex items-center gap-3">
                <div className="flex-1 flex items-center gap-2 bg-red-50 rounded-xl px-3 py-2 border border-red-200">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-sm text-red-600 font-medium">Recording {formatTime(recordingTime)}</span>
                </div>
                <Button
                  data-testid="stop-recording-btn"
                  size="sm"
                  onClick={stopRecording}
                  className="h-9 w-9 p-0 bg-red-500 text-white hover:bg-red-600 rounded-xl flex-shrink-0"
                >
                  <Square className="w-4 h-4 fill-current" />
                </Button>
              </div>
            ) : (
              // Normal input UI
              <div className="flex gap-2 items-end">
                <textarea
                  ref={inputRef}
                  data-testid="chat-input"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendClick(); } }}
                  placeholder="Type or tap mic..."
                  className="flex-1 text-sm border border-stone-200 rounded-xl px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-stone-400"
                  style={{ minHeight: "38px", maxHeight: "80px" }}
                  rows={1}
                  disabled={loading || isTranscribing}
                />
                <Button
                  data-testid="voice-record-btn"
                  size="sm"
                  onClick={startRecording}
                  disabled={loading || isTranscribing}
                  className="h-9 w-9 p-0 bg-stone-100 text-stone-600 hover:bg-stone-200 hover:text-stone-900 rounded-xl flex-shrink-0 border border-stone-200"
                  title="Record voice message"
                >
                  <Mic className="w-4 h-4" />
                </Button>
                <Button
                  data-testid="chat-send"
                  size="sm"
                  onClick={handleSendClick}
                  disabled={!input.trim() || loading || isTranscribing}
                  className="h-9 w-9 p-0 bg-stone-900 text-white hover:bg-stone-800 rounded-xl flex-shrink-0"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Floating Button - hidden on mobile since we have bottom nav */}
      <button
        data-testid="chat-bubble-btn"
        onClick={toggleOpen}
        className={`fixed bottom-4 right-3 sm:bottom-5 sm:right-5 z-50 w-12 h-12 rounded-full shadow-lg items-center justify-center transition-all active:scale-90 hidden md:flex
          ${open ? "bg-stone-600 hover:bg-stone-700 shadow-stone-600/25" : "bg-stone-900 hover:bg-stone-800 shadow-stone-900/30"}`}
      >
        {open ? <X className="w-5 h-5 text-white" /> : <MessageSquare className="w-5 h-5 text-white" />}
      </button>
    </>
  );
}
